/**
 * A history-API router, about eighty lines of it.
 *
 * Hash routing was rejected: this is a shop, the pages need to be indexable
 * and shareable, and `/pricing` has to be a real URL a customer can be sent
 * to. That means the server must rewrite unknown paths to index.html —
 * `deploy/Caddyfile.snippet` does exactly that.
 *
 * A dependency would have added a build target and a version to track for a
 * feature set we use one tenth of.
 */

import type { Component } from 'svelte'
import { ogImagePath } from './og'
import { langFor, TRANSLATION_OF } from './lang'

export interface Route {
  /** Pattern with `:name` segments, e.g. `/docs/:slug`. */
  path: string
  component: () => Promise<{ default: Component<any> }>
  title: string
  description: string
  /** Reserved for when accounts exist; nothing sets it while the shop is shut. */
  requiresAuth?: boolean
  /** Keep out of the sitemap (thank-you pages, auth callbacks). */
  noIndex?: boolean
}

interface Compiled {
  route: Route
  regex: RegExp
  keys: string[]
}

function compile(route: Route): Compiled {
  const keys: string[] = []
  const pattern = route.path
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      keys.push(segment.slice(1))
      return '([^/]+)'
    })
    .join('/')
  return { route, keys, regex: new RegExp(`^${pattern}/?$`) }
}

export class Router {
  private compiled: Compiled[]
  private notFound: Route

  path = $state(normalise(window.location.pathname))
  search = $state(window.location.search)
  params = $state<Record<string, string>>({})
  current = $state<Route | null>(null)
  component = $state<Component<any> | null>(null)
  /** True while a lazy route chunk is in flight. */
  navigating = $state(false)
  /**
   * Committed navigations, counted. Bumped in step with `component`, never
   * before it.
   *
   * `App.svelte` keys the outlet on this rather than on `path`, because
   * `path` moves the instant a link is clicked and `component` only when the
   * chunk resolves. Keying on `path` re-creates the outlet in that gap — a
   * fresh mount of the page you are LEAVING, at the URL of the page you asked
   * for. Keying here means one re-creation, at the moment the new page is
   * ready.
   */
  view = $state(0)

  /** Increments per navigation; a stale chunk load compares unequal and exits. */
  private token = 0

  constructor(routes: Route[], notFound: Route) {
    this.compiled = routes.map(compile)
    this.notFound = notFound

    window.addEventListener('popstate', () => {
      this.path = normalise(window.location.pathname)
      this.search = window.location.search
      void this.resolve()
    })

    // Intercept same-origin left-clicks on plain links so ordinary <a href>
    // works everywhere without every component importing a <Link>.
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = (event.target as HTMLElement)?.closest?.('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || anchor.target === '_blank' || anchor.hasAttribute('download')) return
      if (anchor.hasAttribute('data-external')) return
      const url = new URL(href, window.location.href)
      if (url.origin !== window.location.origin) return
      // Assets are not routes: /news.xml, /sitemap.xml and friends must reach
      // the server, not the SPA's 404 page. Route paths never carry a file
      // extension, so this test cannot eat a real navigation.
      if (/\.[a-z0-9]+$/i.test(url.pathname)) return
      event.preventDefault()
      this.go(url.pathname + url.search + url.hash)
    })
  }

  /** Navigate. `replace` avoids stacking history on redirects. */
  go(to: string, options: { replace?: boolean } = {}): void {
    const url = new URL(to, window.location.href)
    const target = url.pathname + url.search + url.hash
    if (target === window.location.pathname + window.location.search + window.location.hash) return
    history[options.replace ? 'replaceState' : 'pushState']({}, '', target)
    this.path = normalise(url.pathname)
    this.search = url.search
    void this.resolve(url.hash)
  }

  /** Match a path against the table without touching any state. */
  private match(path: string): { route: Route; params: Record<string, string> } {
    for (const { route, regex, keys } of this.compiled) {
      const found = regex.exec(path)
      if (!found) continue
      return {
        route,
        params: Object.fromEntries(
          keys.map((key, index) => [key, decodeURIComponent(found[index + 1])]),
        ),
      }
    }
    return { route: this.notFound, params: {} }
  }

  /** Match the current path, load its chunk, and update the document head. */
  async resolve(hash = window.location.hash): Promise<void> {
    const { route, params } = this.match(this.path)

    // `current` and `params` are published BEFORE awaiting the chunk, so they
    // always describe `path`. Setting them afterwards leaves them one
    // navigation behind for the length of the load, and App.svelte's auth
    // guard reads them — it would see the previous route's `requiresAuth` and
    // redirect a page that needs no session at all.
    this.current = route
    this.params = params

    // Navigations can finish out of order: a large chunk started first can
    // resolve after a small one started second. Only the newest may commit.
    const token = ++this.token
    this.navigating = true

    let module: { default: Component<any> }
    try {
      module = await route.component()
    } catch (error) {
      // A failed chunk is usually a stale index.html pointing at hashed
      // assets that a deploy has replaced. Reloading picks up the new one.
      console.error('route chunk failed to load', error)
      window.location.reload()
      return
    } finally {
      if (token === this.token) this.navigating = false
    }

    if (token !== this.token) return

    this.component = module.default
    // In step with `component`, and only for a navigation that commits: a
    // superseded one returned above without touching either.
    this.view += 1
    applyHead(route, this.path)
    restoreScroll(hash)
  }
}

function normalise(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

const SITE_NAME = 'Mountit'
const ORIGIN = 'https://mountit.app'

/**
 * The path this document was served as.
 *
 * The static copy of that path carries JSON-LD describing it — an Article, a
 * FAQPage — written by `scripts/prerender.mjs`. Those blocks are correct
 * until the first client-side navigation and wrong immediately afterwards, so
 * the first navigation away removes them. The breadcrumb is the exception:
 * `setBreadcrumb` replaces it in place, because it is regenerated per route.
 */
let entryPath: string | null = null

function dropStalePageJsonLd(path: string): void {
  // Captured lazily, not at module scope: this module is also evaluated by
  // the SSR build (BackendArticle and DocArticle import `setPageMeta` from
  // it), where `window` does not exist. The first call happens during the
  // very first `resolve()`, so it captures the path the document was served
  // as, which is what the prerendered blocks describe.
  entryPath ??= path
  if (path === entryPath) return
  for (const script of document.querySelectorAll('script[data-ld-page]')) script.remove()
}

/**
 * 'website' for an index/product page, 'article' for the ones
 * `structured-data.ts`'s `describePages()` marks Article/TechArticle — the
 * same four path families, kept here as a minimal path check rather than an
 * import of that module (which pulls in every content module, and this file
 * runs in the shell bundle that must stay small). Keep the two lists in step.
 */
function ogTypeFor(path: string): 'website' | 'article' {
  if (path.startsWith('/docs/') || path.startsWith('/news/')) return 'article'
  if (path === '/macfuse' || path === '/exit' || path === '/security') return 'article'
  return 'website'
}

function applyHead(route: Route, path: string): void {
  const image = ORIGIN + ogImagePath(path)
  document.title = route.title === SITE_NAME ? SITE_NAME : `${route.title} — ${SITE_NAME}`
  setMeta('name', 'description', route.description)
  setMeta('property', 'og:type', ogTypeFor(path))
  setMeta('property', 'og:locale', OG_LOCALE[langFor(path)])
  setMeta('property', 'og:title', route.title)
  setMeta('property', 'og:description', route.description)
  setMeta('property', 'og:url', ORIGIN + path)
  setMeta('property', 'og:image', image)
  setMeta('property', 'og:image:width', '1200')
  setMeta('property', 'og:image:height', '630')
  setMeta('name', 'twitter:card', 'summary_large_image')
  setMeta('name', 'twitter:title', route.title)
  setMeta('name', 'twitter:description', route.description)
  setMeta('name', 'twitter:image', image)
  setMeta('name', 'robots', route.noIndex ? 'noindex, nofollow' : 'index, follow')
  dropStalePageJsonLd(path)

  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.rel = 'canonical'
    document.head.appendChild(canonical)
  }
  canonical.href = ORIGIN + path

  document.documentElement.lang = langFor(path)
  setHreflang(path)

  setBreadcrumb(breadcrumbFor(route, path))
}

/**
 * The reciprocal `hreflang` pair for a French/English legal page, mirroring
 * `scripts/prerender.mjs`'s `patchLanguage` for a client-side navigation. A
 * page with no counterpart in `TRANSLATION_OF` gets its two links removed
 * rather than left stale from whichever page was open before it.
 */
/** schema.org/OG locale for each language this site publishes in. */
const OG_LOCALE: Record<'en' | 'fr', string> = { en: 'en_GB', fr: 'fr_FR' }

function setHreflang(path: string): void {
  for (const tag of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) tag.remove()
  const alternateLocale = document.head.querySelector('meta[property="og:locale:alternate"]')
  if (alternateLocale) alternateLocale.remove()

  const counterpart = TRANSLATION_OF[path]
  if (!counterpart) return
  for (const [href, hreflang] of [
    [ORIGIN + path, langFor(path)],
    [ORIGIN + counterpart, langFor(counterpart)],
  ] as const) {
    const link = document.createElement('link')
    link.rel = 'alternate'
    link.hreflang = hreflang
    link.href = href
    document.head.appendChild(link)
  }
  const meta = document.createElement('meta')
  meta.setAttribute('property', 'og:locale:alternate')
  meta.setAttribute('content', OG_LOCALE[langFor(counterpart)])
  document.head.appendChild(meta)
}

/**
 * Breadcrumb labels for a path: every segment except the last is prettified
 * (`legal` → `Legal`), and the last uses the route's own title. Dynamic
 * routes (`/docs/:slug`, `/backends/:slug`) refine theirs through
 * `setPageMeta`/`setPageBreadcrumb` once the slug resolves, because the
 * table cannot know which article is showing.
 */
function breadcrumbFor(route: Route, path: string): Crumb[] {
  const segments = path.split('/').filter(Boolean)
  const crumbs: Crumb[] = [{ name: 'Home', url: '/' }]
  let accumulated = ''
  segments.forEach((segment, index) => {
    accumulated += `/${segment}`
    const isLeaf = index === segments.length - 1
    if (isLeaf) {
      crumbs.push({ name: route.title, url: accumulated })
      return
    }
    // Only a segment that is itself a page may become a crumb. `/legal` is
    // not one: the legal set has no index, and a BreadcrumbList whose middle
    // item 404s is worse than a two-item list.
    if (INDEXLESS_SEGMENTS.has(accumulated)) return
    crumbs.push({ name: prettify(segment), url: accumulated })
  })
  return crumbs
}

/** Path prefixes that group pages without being pages. Keep in step with `src/routes.ts`. */
const INDEXLESS_SEGMENTS = new Set(['/legal'])

function prettify(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export interface Crumb {
  name: string
  url: string
}

/** Replace the breadcrumb JSON-LD block, creating it on first navigation. */
export function setBreadcrumb(crumbs: Crumb[]): void {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: ORIGIN + crumb.url,
    })),
  }

  let script = document.querySelector<HTMLScriptElement>('script[data-ld-breadcrumb]')
  if (!script) {
    script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-ld-breadcrumb', '')
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(json)
}

/**
 * For dynamic routes whose real title and description depend on the slug:
 * correct everything the route table had to guess at, in one call. Static
 * routes never need this — the table already carries the right strings.
 */
export function setPageMeta(title: string, description: string): void {
  document.title = `${title} — ${SITE_NAME}`
  setMeta('name', 'description', description)
  setMeta('property', 'og:title', title)
  setMeta('property', 'og:description', description)
  setMeta('name', 'twitter:title', title)
  setMeta('name', 'twitter:description', description)
}

function setMeta(attribute: 'name' | 'property', key: string, value: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.content = value
}

function restoreScroll(hash: string): void {
  // Wait for the new component to paint before scrolling, or the anchor is
  // not in the document yet and the jump silently does nothing.
  requestAnimationFrame(() => {
    if (hash) {
      const target = document.querySelector(hash)
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }
    window.scrollTo(0, 0)
  })
}
